import {
    ReactElement,
    useRef,
    useState,
    Dispatch,
    FormEventHandler,
    forwardRef,
    SetStateAction,
    useImperativeHandle,
    useMemo
} from 'react';
import { CompanyUser } from '@/api';
import { useUpdateCompanyUserAction } from '../actions';
import {
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UISelectInput,
    UISelectValue,
    UISwitch,
    UIText
} from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { getBrowserTimezone, getTimezoneOptions } from '@/common/util';

type TimezoneSettingsSectionProps = {
    companyUser: CompanyUser;
    canEdit: boolean;
    /** Shown on the disabled Edit button when `canEdit` is false. */
    editDisabledReason?: string;
};

export function MemberTimezoneSettingsSection(
    props: TimezoneSettingsSectionProps
): ReactElement {
    const { companyUser, canEdit, editDisabledReason } = props;

    const { t } = useCompaniesTranslation();

    const formRef = useRef<MemberTimezoneSettingsSectionFormRef>(null);
    const updateCompanyUser = useUpdateCompanyUserAction({
        successMessage: t(
            'components.member_timezone_settings_section.toast_updated'
        )
    });
    const [isEditing, setIsEditing] = useState(false);

    function onSubmit(data: {
        timezone: string;
        timezoneAutoDetect: boolean;
        use24HourClock: boolean;
    }) {
        const { timezone, timezoneAutoDetect, use24HourClock } = data;
        updateCompanyUser
            .updateCompanyUser(companyUser.id, {
                timezone,
                timezoneAutoDetect,
                use24HourClock
            })
            .then(() => {
                setIsEditing(false);
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <MemberTimezoneSettingsSectionForm
            ref={formRef}
            canEdit={canEdit}
            editDisabledReason={editDisabledReason}
            initialTimezone={companyUser.timezone}
            initialTimezoneAutoDetect={companyUser.timezoneAutoDetect}
            initialUse24HourClock={companyUser.use24HourClock}
            loading={updateCompanyUser.loading}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            onSubmit={onSubmit}
        />
    );
}

type TimezoneSettingsSectionFormProps = {
    canEdit: boolean;
    /** Shown on the disabled Edit button when `canEdit` is false. */
    editDisabledReason?: string;
    initialTimezone: string;
    initialTimezoneAutoDetect: boolean;
    initialUse24HourClock: boolean;
    loading: boolean;
    isEditing: boolean;
    setIsEditing: Dispatch<SetStateAction<boolean>>;
    onSubmit: (data: {
        timezone: string;
        timezoneAutoDetect: boolean;
        use24HourClock: boolean;
    }) => void;
};

type MemberTimezoneSettingsSectionFormRef = {
    resetForm: () => void;
};

const MemberTimezoneSettingsSectionForm = forwardRef<
    MemberTimezoneSettingsSectionFormRef,
    TimezoneSettingsSectionFormProps
>((props, ref): ReactElement => {
    const {
        canEdit,
        editDisabledReason,
        initialTimezone,
        initialTimezoneAutoDetect,
        initialUse24HourClock,
        loading,
        isEditing,
        setIsEditing,
        onSubmit
    } = props;

    const [timezoneAutoDetect, setTimezoneAutoDetect] = useState(
        initialTimezoneAutoDetect
    );
    const [timezone, setTimezone] = useState(initialTimezone);
    const [use24HourClock, setUse24HourClock] = useState(initialUse24HourClock);

    const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

    const browserTimezone = useMemo(() => getBrowserTimezone(), []);

    const effectiveTimezone = timezoneAutoDetect ? browserTimezone : timezone;

    const selectedTimezoneOption = useMemo((): UISelectValue | undefined => {
        const option = timezoneOptions.find(
            (opt) => opt.value === effectiveTimezone
        );
        return option;
    }, [timezoneOptions, effectiveTimezone]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();
        onSubmit({
            timezone: effectiveTimezone,
            timezoneAutoDetect,
            use24HourClock
        });
    };

    function resetForm() {
        setTimezone(initialTimezone);
        setTimezoneAutoDetect(initialTimezoneAutoDetect);
        setUse24HourClock(initialUse24HourClock);
        setIsEditing(false);
    }

    useImperativeHandle(ref, () => ({
        resetForm() {
            resetForm();
        }
    }));

    return (
        <UISection>
            {isEditing ? (
                <EditableForm
                    timezoneAutoDetect={timezoneAutoDetect}
                    setTimezoneAutoDetect={setTimezoneAutoDetect}
                    selectedTimezoneOption={selectedTimezoneOption}
                    timezoneOptions={timezoneOptions}
                    setTimezone={setTimezone}
                    use24HourClock={use24HourClock}
                    setUse24HourClock={setUse24HourClock}
                    loading={loading}
                    resetForm={resetForm}
                    handleSubmit={handleSubmit}
                />
            ) : (
                <NonEditableForm
                    canEdit={canEdit}
                    editDisabledReason={editDisabledReason}
                    timezoneAutoDetect={timezoneAutoDetect}
                    use24HourClock={use24HourClock}
                    selectedTimezoneOption={selectedTimezoneOption}
                    onEditClick={() => setIsEditing(true)}
                />
            )}
        </UISection>
    );
});

function NonEditableForm(props: {
    canEdit: boolean;
    editDisabledReason?: string;
    timezoneAutoDetect: boolean;
    use24HourClock: boolean;
    selectedTimezoneOption: UISelectValue | undefined;
    onEditClick: () => void;
}): ReactElement {
    const {
        canEdit,
        editDisabledReason,
        timezoneAutoDetect,
        use24HourClock,
        selectedTimezoneOption,
        onEditClick
    } = props;

    const { t } = useCompaniesTranslation();

    return (
        <>
            <UISectionHeader
                title={t('components.member_timezone_settings_section.title')}
                rightButtons={[
                    {
                        text: t('shared.labels.edit'),
                        variant: 'textDefault',
                        type: 'button',
                        disabled: !canEdit,
                        disabledTooltip: editDisabledReason,
                        onClick: onEditClick
                    }
                ]}
            />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <UIText>
                                {t(
                                    'components.member_timezone_settings_section.auto_detect_label'
                                )}
                            </UIText>
                            <UIText type="default2">
                                {t(
                                    'components.member_timezone_settings_section.auto_detect_description'
                                )}
                            </UIText>
                        </div>
                        <div style={{ display: 'flex', flex: 1 }}>
                            <UIText
                                medium
                                type={timezoneAutoDetect ? 'link' : 'error'}
                            >
                                {timezoneAutoDetect
                                    ? t(
                                          'components.member_timezone_settings_section.enabled'
                                      )
                                    : t(
                                          'components.member_timezone_settings_section.disabled'
                                      )}
                            </UIText>
                        </div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <UIText>
                                {t(
                                    'components.member_timezone_settings_section.timezone_label'
                                )}
                            </UIText>
                            <UIText type="default2">
                                {t(
                                    'components.member_timezone_settings_section.timezone_description'
                                )}
                            </UIText>
                        </div>
                        <div style={{ display: 'flex', flex: 1 }}>
                            <UIText medium>
                                {selectedTimezoneOption?.label ??
                                    t(
                                        'components.member_timezone_settings_section.not_set'
                                    )}
                            </UIText>
                        </div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <UIText>
                                {t(
                                    'components.member_timezone_settings_section.clock_label'
                                )}
                            </UIText>
                            <UIText type="default2">
                                {t(
                                    'components.member_timezone_settings_section.clock_description'
                                )}
                            </UIText>
                        </div>
                        <div style={{ display: 'flex', flex: 1 }}>
                            <UIText
                                medium
                                type={use24HourClock ? 'link' : 'error'}
                            >
                                {use24HourClock
                                    ? t(
                                          'components.member_timezone_settings_section.enabled'
                                      )
                                    : t(
                                          'components.member_timezone_settings_section.disabled'
                                      )}
                            </UIText>
                        </div>
                    </div>
                </div>
            </UISectionContent>
        </>
    );
}

function EditableForm(props: {
    timezoneAutoDetect: boolean;
    setTimezoneAutoDetect: Dispatch<SetStateAction<boolean>>;
    selectedTimezoneOption: UISelectValue | undefined;
    timezoneOptions: UISelectValue[];
    setTimezone: (value: string) => void;
    use24HourClock: boolean;
    setUse24HourClock: Dispatch<SetStateAction<boolean>>;
    loading: boolean;
    resetForm: () => void;
    handleSubmit: FormEventHandler;
}): ReactElement {
    const {
        timezoneAutoDetect,
        setTimezoneAutoDetect,
        selectedTimezoneOption,
        timezoneOptions,
        setTimezone,
        use24HourClock,
        setUse24HourClock,
        loading,
        resetForm,
        handleSubmit
    } = props;

    const { t } = useCompaniesTranslation();

    return (
        <form onSubmit={handleSubmit} noValidate>
            <UISectionHeader
                title={t('components.member_timezone_settings_section.title')}
            />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <UIText>
                                {t(
                                    'components.member_timezone_settings_section.auto_detect_label'
                                )}
                            </UIText>
                            <UIText type="default2">
                                {t(
                                    'components.member_timezone_settings_section.auto_detect_description'
                                )}
                            </UIText>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flex: 1
                            }}
                        >
                            <UISwitch
                                checked={timezoneAutoDetect}
                                onChange={setTimezoneAutoDetect}
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <UIText>
                                {t(
                                    'components.member_timezone_settings_section.timezone_label'
                                )}
                            </UIText>
                            <UIText type="default2">
                                {t(
                                    'components.member_timezone_settings_section.timezone_description'
                                )}
                            </UIText>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flex: 1
                            }}
                        >
                            <UISelectInput
                                value={selectedTimezoneOption}
                                options={timezoneOptions}
                                onChange={(option) => {
                                    if (option) {
                                        setTimezone(option.value);
                                    }
                                }}
                                disabled={timezoneAutoDetect}
                                clearable={false}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <UIText>
                                {t(
                                    'components.member_timezone_settings_section.clock_label'
                                )}
                            </UIText>
                            <UIText type="default2">
                                {t(
                                    'components.member_timezone_settings_section.clock_description'
                                )}
                            </UIText>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flex: 1
                            }}
                        >
                            <UISwitch
                                checked={use24HourClock}
                                onChange={setUse24HourClock}
                            />
                        </div>
                    </div>
                </div>
            </UISectionContent>
            <UISectionFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        loading: loading,
                        disabled: loading,
                        text: loading
                            ? t('shared.labels.saving')
                            : t('shared.labels.save')
                    },
                    {
                        text: t('shared.labels.cancel'),
                        type: 'button',
                        disabled: loading,
                        onClick: resetForm
                    }
                ]}
            />
        </form>
    );
}
